import type {ExperimentScope, ExperimentStore} from './store-types'
import {EXPERIMENT_ASSIGNMENT_PROTOCOL_VERSION} from './store-types'
import {
  decodeExperimentAssignment,
  equivalentScopes,
  unavailableExperimentScope,
  withAssignmentContext,
} from './store-validators'
import type {ExperimentDecision} from './experiment-types'

const unavailableScope = unavailableExperimentScope()

/** Creates an isolated assignment store for one SSR request or browser document. */
export function createExperimentStore(initialScope?: ExperimentScope): ExperimentStore {
  let scope: ExperimentScope = unavailableScope
  let assignmentContext = ''
  let initialScopeInstalled = initialScope !== undefined
  let initialScopeConflicted = false
  let compatible = true
  let activeNavigationId: string | undefined
  let snapshot = 0
  const pins = new Map<string, ExperimentDecision>()
  const candidates = new Map<string, ExperimentScope>()
  const listeners = new Set<() => void>()

  const notify = () => {
    snapshot++
    for (const listener of listeners) listener()
  }

  /**
   * Applies a scope to the store.
   *
   * Available scopes merge assignment context; ordinary unavailable scopes retain accumulated context.
   * If the merged context or envelope exceeds its size limit, sets the scope to unavailable
   * and clears assignment context.
   * @returns Whether the scope or assignment context was updated.
   */
  function applyScope(candidate: ExperimentScope): boolean {
    const nextScope = withAssignmentContext(candidate, assignmentContext)
    let nextAssignmentContext = assignmentContext
    if (nextScope.status === 'available') {
      nextAssignmentContext = nextScope.envelope.assignment.assignment_context
    } else if (nextScope.contextMergeFailed) {
      nextAssignmentContext = ''
    }

    // Unavailable scopes represent no usable assignment, so they have no assignment envelope or context.
    // The store can still retain context from earlier assignments. Compare it separately so a size-limit
    // failure clears it even when both scopes are unavailable.
    if (equivalentScopes(scope, nextScope) && assignmentContext === nextAssignmentContext) return false
    scope = nextScope
    assignmentContext = nextAssignmentContext
    return true
  }

  if (initialScope) applyScope(initialScope)

  function startNavigation(navigationId: string) {
    if (navigationId === activeNavigationId) return
    activeNavigationId = navigationId
    candidates.clear()
  }

  return {
    protocolVersion: EXPERIMENT_ASSIGNMENT_PROTOCOL_VERSION,
    get compatible() {
      return compatible
    },
    getScope: () => scope,
    getAssignmentContext: () => assignmentContext,
    installInitialScope(nextScope) {
      if (initialScopeConflicted) return
      if (!initialScopeInstalled) {
        initialScopeInstalled = true
        if (applyScope(nextScope)) notify()
        return
      }
      if (equivalentScopes(scope, nextScope)) return
      // One page can host multiple React app roots. They must agree before sharing the document store.
      initialScopeConflicted = true
      scope = unavailableScope
      notify()
    },
    markIncompatible() {
      if (!compatible) return
      compatible = false
      initialScopeConflicted = true
      scope = unavailableScope
      assignmentContext = ''
      activeNavigationId = undefined
      candidates.clear()
      notify()
    },
    getPin: path => pins.get(path),
    compareAndSetPin(path, decision) {
      const existing = pins.get(path)
      if (existing) return existing
      // The first committed render pins this path for the current document.
      // Later soft navigations reuse the pinned decision even when their assignment scopes differ.
      pins.set(path, decision)
      notify()
      return decision
    },
    startNavigation,
    captureNavigationCandidate(navigationId, candidate) {
      // Ignore late responses from an interrupted or superseded navigation.
      if (navigationId !== activeNavigationId) return
      candidates.set(navigationId, candidate)
    },
    getNavigationCandidate(navigationId) {
      return candidates.get(navigationId) ?? unavailableScope
    },
    commitNavigation(navigationId) {
      if (navigationId !== activeNavigationId) return
      const candidate = candidates.get(navigationId)
      candidates.clear()
      activeNavigationId = undefined
      // Promote only after the destination commits so abandoned renders cannot change the document scope.
      if (applyScope(candidate ?? unavailableScope)) notify()
    },
    discardNavigation(navigationId) {
      if (navigationId !== activeNavigationId) return
      candidates.clear()
      activeNavigationId = undefined
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    getSnapshot: () => snapshot,
    resetForTests() {
      scope = unavailableScope
      assignmentContext = ''
      activeNavigationId = undefined
      pins.clear()
      candidates.clear()
      initialScopeInstalled = false
      initialScopeConflicted = false
      notify()
    },
  }
}

// Symbol.for lets independently loaded bundles share one experiment store on the same document.
const DOCUMENT_STORE_KEY = Symbol.for('github-ui.experiments.document-store')
type DocumentStoreSlot = Readonly<{protocolVersion: number; store: ExperimentStore}>

// A protocol mismatch fails closed instead of allowing bundles to maintain divergent pins.
const incompatibleStore: ExperimentStore = {
  ...createExperimentStore(),
  protocolVersion: EXPERIMENT_ASSIGNMENT_PROTOCOL_VERSION,
  compatible: false,
  getAssignmentContext: () => '',
  installInitialScope() {},
  markIncompatible() {},
  compareAndSetPin: (_path, decision) => decision,
  startNavigation() {},
  captureNavigationCandidate() {},
  commitNavigation() {},
  discardNavigation() {},
}

/** Returns the protocol-compatible store shared by all experiment consumers in a document. */
export function getOrCreateDocumentExperimentStore(targetDocument: Document): ExperimentStore {
  const existing = Reflect.get(targetDocument, DOCUMENT_STORE_KEY) as DocumentStoreSlot | undefined
  if (existing) {
    if (existing.protocolVersion === EXPERIMENT_ASSIGNMENT_PROTOCOL_VERSION) return existing.store
    existing.store.markIncompatible()
    return incompatibleStore
  }

  const store = createExperimentStore()
  Object.defineProperty(targetDocument, DOCUMENT_STORE_KEY, {
    value: Object.freeze({protocolVersion: EXPERIMENT_ASSIGNMENT_PROTOCOL_VERSION, store}),
    configurable: true,
  })
  return store
}

/**
 * Uses the provided request store during SSR and otherwise reuses the browser document store.
 * Callers are responsible for initializing the request and document stores with equivalent
 * assignment scopes so hydration renders the same experiment decision.
 */
export function resolveExperimentStore({
  ssrExperimentStore,
  document: targetDocument,
}: {
  ssrExperimentStore?: ExperimentStore
  document?: Document
}): ExperimentStore {
  if (ssrExperimentStore) return ssrExperimentStore
  if (targetDocument) return getOrCreateDocumentExperimentStore(targetDocument)
  return createExperimentStore()
}

export function installExperimentAssignment(targetDocument: Document, value: unknown): ExperimentStore {
  const store = getOrCreateDocumentExperimentStore(targetDocument)
  store.installInitialScope(decodeExperimentAssignment(value))
  return store
}

export function resetDocumentExperimentStoreForTests(targetDocument: Document): void {
  getOrCreateDocumentExperimentStore(targetDocument).resetForTests()
}

export function setDocumentExperimentProtocolVersionForTests(
  targetDocument: Document,
  protocolVersion: number,
): ExperimentStore {
  const store = createExperimentStore()
  Object.defineProperty(targetDocument, DOCUMENT_STORE_KEY, {
    value: Object.freeze({protocolVersion, store}),
    configurable: true,
  })
  return store
}
